<template>
    <Tree
        ref="tree"
        v-model:expanded-keys="expandedKeys"
        class="tree"
        selection-mode="single"
        :value="courseResourcesTreeNodes"
        :pt="{
            nodeContent: {
                class: 'tw:hover:bg-[var(--surface-hover)] tw:cursor-pointer'
            },
            nodeChildren: {
                style: 'padding-inline-start: 1rem !important;'
            },
            nodeToggleButton: {
                class: 'tw:order-last tw:ml-auto!'
            }
        }"
        @node-select="onNodeSelect"
    >
        <template #default="{ node }">
            <!-- Render dynamic component ONLY for leaf nodes (no children) with a type -->
            <component
                :is="getComponentForItem(node.data)"
                v-if="node.data && getComponentForItem(node.data) && !node.children"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium!"
                :item="node.data"
                :close-menu="closeMenu"
            />

            <!-- For nodes with children and a type, just render the label (Tree handles expansion) -->
            <span
                v-else-if="node.data && node.children"
                :class="{ 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }"
            >
                {{ node.text ?? node.label }}
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:min-w-2 tw:min-h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2"
                    title="Current Week"
                />
            </span>

            <!-- Render router-link if node has a route -->
            <router-link
                v-else-if="node.route"
                :to="node.route"
                class="tw:text-[var(--p-tree-node-color)]! tw:font-medium!"
                :class="{ 'is-active': node.isActive, 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }"
                @click="closeMenu"
            >
                {{ node.label }}
                <span
                    v-if="node.isActive"
                    class="tw:inline-block tw:min-w-2 tw:min-h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
                />
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:w-2 tw:h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2"
                    title="Current Week"
                />
                <e-tag v-if="node.badge" type="is-link" size="is-small" rounded class="tw:ml-2">
                    <strong>{{ node.badge }}</strong>
                </e-tag>
            </router-link>

            <!-- For nodes with children and no type/route, just render the label -->
            <span v-else-if="node.children" :class="{ 'tw:text-[var(--primary-color)]!': node.isCurrentWeek }">
                {{ node.text ?? node.label }}
                <span
                    v-if="node.isCurrentWeek"
                    class="tw:inline-block tw:w-2 tw:h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2"
                    title="Current Week"
                />
            </span>

            <!-- Fallback: just render the label -->
            <span v-else>{{ node.label }}</span>
        </template>
    </Tree>

    <!-- Course Log Modal -->
    <e-modal v-model="openCourseLog">
        <course-log-entries :course-id="courseId" />
    </e-modal>
</template>
<script>
import { pascalCase } from 'change-case';
import capitalize from 'lodash/capitalize.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import { computed } from 'vue';
import { mapState } from 'vuex';
import CourseLogEntries from '@/components/course-log/CourseLogEntries.vue';
import CourseReportsMenu from '@/components/nested-list/CourseReportsMenu.vue';
import NestedList from '@/components/nested-list/NestedList.vue';
import * as NestedListItems from '@/components/nested-list/nested-list-item/Index.js';
import MenuLink from '@/components/nested-list/nested-list-item/MenuLink.vue';
import useSelfPacedCourse from '@/composables/useSelfPacedCourse.js';
import { useLayout } from '@/layouts/composables/layout.js';
import Course from '@/models/Course.js';
import Remediation from '@/models/Remediation.js';
import moment from '@/plugins/vue-moment.js';

export default {
    name: 'CourseMenu',

    components: {
        CourseReportsMenu,
        CourseLogEntries,
        MenuLink,
        NestedList,
        ...NestedListItems.default,
    },

    emits: ['close-drawer'],

    props: {
        items: {
            default: () => ({}),
            type: Object,
        },

        courseId: {
            type: Number,
            required: true,
        },

        mode: {
            type: String,
            default: 'breadcrumb', // 'breadcrumb' or 'sidebar'
            validator: value => ['breadcrumb', 'sidebar'].includes(value),
        },

        itemToOpen: {
            type: String,
            default: undefined,
        },

        navigateAndClose: {
            type: Function,
            default: undefined,
        },
    },

    setup(props) {
        const course = computed(() => Course.query().with(['groups']).find(props.courseId));
        const { isSelfPacedCourse } = useSelfPacedCourse({ course });
        return { isSelfPacedCourse };
    },

    data: () => ({
        openCourseLog: false,
        isOpen: false,
        currentLabel: '',
        labelSet: false,
        expandedKeys: {},
    }),

    watch: {
        '$route.name': {
            handler() {
                this.labelSet = false;
                this.$nextTick(() => {
                    this.setInitialLabel();
                    this.expandToActiveNode();
                });
            },
        },
    },

    mounted() {
        this.setInitialLabel();

        if (this.itemToOpen) {
            this.expandedKeys = {
                [this.itemToOpen]: true,
            };
        }
        this.expandInitialNodes();
        this.expandToActiveNode();
    },

    updated() {
        // Try to set label again if it hasn't been set yet
        // This catches cases where nested lists render after mount
        if (!this.labelSet) {
            this.setInitialLabel();
        }
    },

    // eslint-disable-next-line vue/order-in-components
    computed: {
        ...mapState('Auth', { user: state => state.user }),

        course() {
            return Course.query().with(['groups']).find(this.courseId);
        },

        editAccess() {
            if (this.course && this.user.hasRoleWithinCourseGroup(this.course, 'Content Editor')) {
                const course = this.$store.getters['Courses/get'](this.course.id);
                const array = [new Date(course.course_settings.start_date) < new Date(new Date()), course.in_progress];

                return !array.includes(true);
            }

            return true;
        },

        remediations() {
            return Remediation.query().where('course_id', this.courseId).get();
        },

        pendingGradeRemediations() {
            return this.remediations.filter(remediation => {
                return remediation.status === 'Pending Instructor Grade';
            });
        },

        hasRemediation() {
            if (
                !isEmpty(Remediation.query().where('course_id', this.courseId).get()) ||
                this.course.remediation_activities
            ) {
                return true;
            }

            return false;
        },

        canViewCourse() {
            return (
                this.user && (this.user.hasPermissionViaCourseTo(this.course, 'view a course') || this.user.is_admin)
            );
        },

        canViewRemediation() {
            return (
                this.user.hasGroupPermissionWithinCourse(this.courseId, 'view a remediation process') ||
                this.user.hasPermissionTo('view all remediation processes') ||
                this.user.isInstructorOf(this.courseId)
            );
        },

        canViewRemediationProcesses() {
            return (
                this.pendingGradeRemediations.length > 0 && this.user.hasPermissionTo('view all remediation processes')
            );
        },

        courseResourcesTreeNodes() {
            const nodes = [];

            nodes.push({
                key: 'course-dashboard',
                label: 'Course Dashboard',
                isActive: this.$route.name === 'course-dashboard',
                route: { name: 'course-dashboard', params: { courseId: this.courseId } },
            });

            if (this.$gate.allows('viewPreview', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'gradebook-preview',
                    label: 'Gradebook Preview',
                    isActive: this.$route.name === 'course-gradebook-preview',
                    route: { name: 'course-gradebook-preview', params: { courseId: this.courseId } },
                });
            }

            if (this.$gate.allows('view', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'gradebook',
                    label: 'Gradebook',
                    isActive: this.$route.name === 'gradebook-view',
                    route: {
                        name: 'gradebook-view',
                        params: {
                            courseId: this.courseId,
                            gradebookId: get(this.course, 'gradebook.id'),
                        },
                    },
                });
            }

            if (this.$gate.allows('viewStudent', ['Gradebook', this.course])) {
                nodes.push({
                    key: 'grades',
                    label: 'Grades',
                    isActive: this.$route.name === 'student-grade-view',
                    route: {
                        name: 'student-grade-view',
                        params: {
                            courseId: this.courseId,
                            userId: this.user.id,
                        },
                    },
                });
            }

            if (this.canViewCourse && this.items.meta && this.items.meta.length) {
                const metaChildren = this.convertNestedListToTreeNodes(this.items.meta, 'meta');
                if (metaChildren.length > 0) {
                    nodes.push({
                        key: 'meta',
                        label: 'Meta',
                        children: metaChildren,
                    });
                }
            }

            if (this.$gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', this.course])) {
                nodes.push({
                    key: 'enrollments',
                    label: 'Enrollments',
                    isActive: this.$route.name === 'course-enrollments-view',
                    route: { name: 'course-enrollments-view', params: { courseId: this.courseId } },
                });
            }

            // Course Content
            if (
                this.user &&
                this.items.curriculum &&
                this.items.curriculum.misc_pre &&
                this.items.curriculum.misc_pre.length
            ) {
                const courseContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_pre,
                    'course-content'
                );
                if (courseContentChildren.length > 0) {
                    courseContentChildren.forEach(item => {
                        nodes.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            // Previous Weeks
            const previousChildren = [];

            if (this.canViewCourse && this.weeks('past')) {
                const pastWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.past,
                    'previous-weeks'
                );
                if (pastWeeksChildren.length > 0) {
                    pastWeeksChildren.forEach(item => {
                        previousChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            if (previousChildren.length > 0) {
                nodes.push({
                    key: 'previous-weeks',
                    label: 'Previous Weeks',
                    children: previousChildren,
                });
            }

            // Current Weeks
            if (this.canViewCourse && this.weeks('present')) {
                const currentWeeksChildren = this.convertNestedListToTreeNodes(this.weeks('present'), 'current-weeks');
                currentWeeksChildren.forEach(item => {
                    nodes.push({
                        key: item.key,
                        label: item.label,
                        data: item.data,
                        children: item.children,
                        isCurrentWeek: true,
                    });
                });
            }

            // Upcoming Weeks
            const upcomingChildren = [];

            if (this.canViewCourse && this.weeks('future')) {
                const futureWeeksChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.weeks.future,
                    'future-weeks'
                );
                if (futureWeeksChildren.length > 0) {
                    futureWeeksChildren.forEach(item => {
                        upcomingChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            if (
                this.canViewCourse &&
                this.items.curriculum &&
                this.items.curriculum.misc_post &&
                this.items.curriculum.misc_post.length
            ) {
                const additionalContentChildren = this.convertNestedListToTreeNodes(
                    this.items.curriculum.misc_post,
                    'additional-content'
                );
                if (additionalContentChildren.length > 0) {
                    additionalContentChildren.forEach(item => {
                        upcomingChildren.push({
                            key: item.key,
                            label: item.label,
                            data: item.data,
                            children: item.children,
                        });
                    });
                }
            }

            if (upcomingChildren.length > 0) {
                nodes.push({
                    key: 'upcoming-weeks',
                    label: 'Upcoming Weeks',
                    children: upcomingChildren,
                });
            }

            // Course Reports
            if (this.$gate.allows('viewReports', this.course)) {
                const reportChildren = [];

                reportChildren.push({
                    key: 'instructor-engagement',
                    label: 'Instructor Engagement',
                    isActive: this.$route.name === 'discussion-thread-report',
                    route: { name: 'discussion-thread-report' },
                });

                nodes.push({
                    key: 'course-reports',
                    label: 'Course Reports',
                    children: reportChildren,
                });
            }

            if (!this.isSelfPacedCourse && this.$gate.allows('viewAny', ['Attendance', this.course])) {
                nodes.push({
                    key: 'attendance',
                    label: 'Attendance',
                    isActive: this.$route.name === 'course-attendance',
                    route: { name: 'course-attendance', params: { courseId: this.courseId } },
                });
            }

            if (this.$gate.allows('viewAny', ['Meeting', this.courseId]) && this.$hasFeature('MEETINGS')) {
                nodes.push({
                    key: 'meetings',
                    label: 'Meetings',
                    isActive: this.$route.name === 'course-meetings-view-upcoming',
                    route: { name: 'course-meetings-view-upcoming' },
                });
            }

            if (this.$gate.allows('viewAny', ['AttemptAuthorization', this.course])) {
                nodes.push({
                    key: 'attempt-authorizations',
                    label: 'Attempt Authorizations',
                    isActive: this.$route.name === 'course-attempt-authorizations-view',
                    route: { name: 'course-attempt-authorizations-view', params: { courseId: this.courseId } },
                });
            }

            nodes.push({
                key: 'my-notes',
                label: 'My Notes',
                isActive: this.$route.name === 'course-notes',
                route: { name: 'course-notes' },
            });

            if (this.canViewRemediation) {
                nodes.push({
                    key: 'remediation',
                    label: 'Remediation',
                    isActive: this.$route.name === 'course-remediation',
                    route: { name: 'course-remediation' },
                    badge: this.canViewRemediationProcesses ? this.pendingGradeRemediations.length : null,
                });
            }

            if (this.course && this.$gate.allows('viewAny', ['CourseLogEntry', this.course])) {
                nodes.push({
                    key: 'course-log',
                    label: 'Course Log',
                    action: 'openCourseLog',
                    badge: this.course.log_entries_count > 0 ? this.course.log_entries_count : null,
                });
            }

            nodes.push({
                key: 'due-dates',
                label: 'Due Dates and Schedule',
                isActive: this.$route.name === 'course-activities-view-upcoming',
                route: { name: 'course-activities-view-upcoming', params: { courseId: this.courseId } },
            });

            return nodes;
        },

        treeNodes() {
            const nodes = [];

            return nodes;
        },
    },

    methods: {
        get,

        toggleMenu() {
            this.isOpen = !this.isOpen;
        },

        closeMenu() {
            if (this.$isMobile || this.$isTablet) {
                useLayout().toggleSidebarSummary();
                this.$emit('close-drawer');
            }
            this.isOpen = false;
        },

        expandInitialNodes() {
            this.courseResourcesTreeNodes.forEach(node => {
                if (node.key.startsWith('current-weeks')) {
                    this.expandNode(node);
                }
            });

            this.expandedKeys = { ...this.expandedKeys };
        },

        expandToActiveNode() {
            const route = this.$route;

            const expandAncestors = ancestors => {
                ancestors.forEach(key => {
                    this.expandedKeys[key] = true;
                });
                this.expandedKeys = { ...this.expandedKeys };
            };

            const isRouteMatch = node => {
                // Check nodes with explicit route (top-level items like Gradebook, Attendance, etc.)
                if (node.route && node.route.name === route.name) {
                    return Object.keys(node.route.params || {}).every(
                        key => String(node.route.params[key]) === String(route.params[key])
                    );
                }

                // Check nodes with route_name in data (items from convertNestedListToTreeNodes)
                if (node.data?.route_name === route.name) {
                    const params = node.data.route_params || {};
                    return Object.keys(params).every(key => String(params[key]) === String(route.params[key]));
                }

                // Check activity nodes rendered by child components (Activity.vue, etc.)
                // These have data.activity_id but no route_name — the route is computed in the component
                if (node.data?.activity_id && route.name === 'activity-view') {
                    return (
                        String(node.data.activity_id) === String(route.params.activityId) &&
                        String(node.data.course_id) === String(route.params.courseId)
                    );
                }

                return false;
            };

            const findAndExpand = (nodes, ancestors = []) =>
                nodes.some(node => {
                    if (isRouteMatch(node)) {
                        expandAncestors(ancestors);
                        return true;
                    }

                    if (node.children) {
                        return findAndExpand(node.children, [...ancestors, node.key]);
                    }

                    return false;
                });

            findAndExpand(this.courseResourcesTreeNodes);
        },

        expandNode(node) {
            if (node.children && node.children.length) {
                this.expandedKeys[node.key] = true;

                node.children.forEach(child => {
                    this.expandNode(child);
                });
            }
        },

        /**
         * Get the component name for a given item type
         * Same logic as the old NestedList component
         */
        getComponentForItem(item) {
            if (!item || !item.type) return null;
            return pascalCase(item.type);
        },

        setInitialLabel() {
            // Wait for DOM to be ready and router links to be rendered
            this.$nextTick(() => {
                const currentPath = this.$route.path;

                // Find all links in the component
                const treeEl = this.$refs.tree?.$el;
                if (!treeEl) return;

                const links = treeEl.querySelectorAll('a');

                // Find the link whose href matches the current path
                const activeLink = Array.from(links).find(link => {
                    const href = link.getAttribute('href');
                    return href && href === currentPath;
                });

                if (activeLink) {
                    // Extract the text content from the active link
                    const labelText = activeLink.textContent?.trim();
                    // Clean up the text (remove extra whitespace, numbers, etc.)
                    const cleanedLabel = labelText?.replace(/\s+/g, ' ').trim();

                    if (cleanedLabel) {
                        this.currentLabel = cleanedLabel;
                        this.labelSet = true;
                    }
                }
            });
        },

        /**
         * @param tense
         * @return {Array|boolean}
         */
        weeks(tense) {
            if (!get(this.items, 'curriculum.weeks')) {
                return false;
            }

            const weeks = this.items.curriculum.weeks[tense];

            if (!weeks || !weeks.length) {
                return false;
            }

            if (weeks.length === 1) {
                return weeks;
            }

            return [
                {
                    id: tense,
                    prerequisite: null,
                    section_type: 'topic',
                    text: this.getWeekContainerTitle(tense),
                    title: this.getWeekContainerTitle(tense),
                    type: 'section',
                    visible_at: null,
                    items: weeks,
                },
            ];
        },

        /**
         * @param tense
         * @return {string}
         */
        getWeekContainerTitle(tense) {
            return tense === 'past' ? 'Previous Weeks' : 'Upcoming Weeks';
        },

        onNodeSelect(node) {
            // Handle special actions
            if (node.action === 'openCourseLog') {
                this.openCourseLog = true;
                return;
            }

            // Handle navigation via route
            if (node.route) {
                return;
            }

            // Toggle expansion for nodes with children
            if (node.children && node.children.length > 0) {
                if (this.expandedKeys[node.key]) {
                    const newKeys = { ...this.expandedKeys };
                    delete newKeys[node.key];
                    this.expandedKeys = newKeys;
                } else {
                    this.expandedKeys = {
                        ...this.expandedKeys,
                        [node.key]: true,
                    };
                }
            }
        },

        formatItemLabel(item) {
            const text = item.text || item.title || '';

            if (item.type !== 'section' || item.section_type === 'topic') {
                return text || 'Untitled';
            }

            const hasTitle = text && !item.has_fallback_text;
            const optionalTitle = hasTitle ? `: ${text}` : '';
            const type = capitalize(item.section_type);

            let dateRange = '';
            if (item.week_start && item.week_end && !this.$route.name?.includes('temp')) {
                const carriesOver = moment(item.week_end).isAfter(item.week_start, 'year');
                const format = carriesOver ? 'MM/DD/YY' : 'MM/DD';
                const weekStart = moment.utc(item.week_start).tz(this.$timezone.name).add(1, 'hours').format(format);
                const weekEnd = moment.utc(item.week_end).tz(this.$timezone.name).subtract(1, 'hours').format(format);
                dateRange = ` (${weekStart}-${weekEnd})`;
            }

            if (item.section_type === 'break') {
                return `${type}${dateRange}${optionalTitle}`;
            }

            if (item.section_type === 'week') {
                const weekNum = item.display_week_number || item.week_number;
                return `${type} ${weekNum}${dateRange}${optionalTitle}`;
            }

            return text || 'Untitled';
        },

        convertNestedListToTreeNodes(items, parentKey = '') {
            if (!items || !Array.isArray(items)) return [];

            return items
                .filter(item => this.hasNestedListPermission(item))
                .map((item, index) => {
                    const key = `${parentKey}-${item.type || 'item'}-${item.id || index}`;
                    const node = {
                        key,
                        label: this.formatItemLabel(item),
                        data: item,
                        ...(item.items &&
                            item.items.length > 0 && { children: this.convertNestedListToTreeNodes(item.items, key) }),
                    };

                    // Handle route navigation if item has route info
                    if (item.route_name) {
                        node.route = {
                            name: item.route_name,
                            params: item.route_params || {},
                        };
                        node.isActive = this.$route.name === item.route_name;
                    }

                    return node;
                });
        },

        hasNestedListPermission(item) {
            if (!item.permission) return true;

            const course = Course.query().with(['groups']).find(this.courseId);

            return (
                this.$can(item.permission.action, item.permission.subject) ||
                this.user.hasGroupPermissionWithinCourse(course, item.permission.name)
            );
        },
    },
};
</script>

<style scoped lang="scss">
.p-tree {
    padding: 0;
    padding-block: 0.5rem;
}

:deep(.p-tree-node-content) {
    padding: 0;
    padding-block: 0.25rem;
}

:deep(.p-tree-node-content:has(a.is-active)) {
    background-color: var(--surface-hover);
}

:deep(a.is-active) {
    color: var(--primary-color) !important;
    font-weight: 700 !important;
}
</style>
